
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function BreakerPanelTestingPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Breaker Panel Testing & Inspection',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Breaker panel testing and inspection in Fresno, CA. Ensure your electrical panel and circuit breakers are safe, functional, and code-compliant.',
    url: `${siteUrl}/services/testing-certification/breaker-panel-testing`,
  };

  const features = [
    {
      icon: 'ri-tools-line',
      title: 'Breaker Performance Testing',
      description:
        'Test circuit breakers for proper operation, tripping characteristics, and safety performance',
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Thermal Imaging',
      description:
        'Infrared scans detect overheating breakers, loose connections, and hot spots in panels',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Panel Safety Inspection',
      description:
        'Comprehensive inspection of panel condition, wiring, and safety compliance',
    },
    {
      icon: 'ri-alert-line',
      title: 'Hazardous Panel Identification',
      description:
        'Identify dangerous panels like Federal Pacific, Zinsco, and other recalled brands',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Code Compliance Review',
      description:
        'Verify panel installation meets California Title 24 and NEC requirements',
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Connection Testing',
      description:
        'Test all connections, bus bars, and terminals for proper tightness and conductivity',
    },
    {
      icon: 'ri-home-4-line',
      title: 'Capacity Assessment',
      description:
        'Evaluate panel capacity and determine if upgrades are needed for your electrical demands',
    },
    {
      icon: 'ri-file-text-line',
      title: 'Detailed Inspection Report',
      description:
        'Comprehensive report with findings, photos, and recommendations for your panel',
    },
  ];

  const whatsIncluded = [
    'Licensed C-10 electrician inspection',
    'Circuit breaker performance testing',
    'Thermal imaging scans',
    'Panel condition assessment',
    'Connection tightness testing',
    'Voltage and amperage measurements',
    'Hazardous panel identification',
    'Code compliance verification',
    'Capacity evaluation',
    'Detailed written report with photos',
  ];

  return (
    <>
      <SEOHead
        title="Breaker Panel Testing Fresno CA | Electrical Panel Inspection & Testing"
        description="Breaker panel testing and inspection in Fresno, CA. Ensure your electrical panel and circuit breakers are safe and functional. Licensed electricians available now."
        canonical="/services/testing-certification/breaker-panel-testing"
        keywords="breaker panel testing Fresno, electrical panel inspection, circuit breaker testing, panel safety inspection Fresno"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Breaker Panel Testing & Inspection — Fresno, CA"
        heroSubtitle="Ensure your electrical panel and circuit breakers are safe and reliable"
        heroImage="https://public.readdy.ai/ai/img_res/9ae95e8683a8a162583366fa4eeb8f34.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20electrical%20panel%20testing%20equipment%20including%20thermal%20imaging%20camera%20digital%20multimeter%20and%20circuit%20tester%20on%20table%20next%20to%20open%20breaker%20panel%20with%20circuit%20breakers%20and%20bus%20bars%20visible%20bright%20interior%20setting&width=700&height=600&seq=panel-test-fresno-001&orientation=portrait"
        fresnoContent="Your electrical panel is the heart of your home's electrical system. Fresno homeowners trust our licensed electricians for thorough breaker panel testing and inspection services. We test circuit breakers for proper operation, use thermal imaging to detect overheating and loose connections, identify hazardous panels, and verify code compliance. Whether you're in Woodward Park, Tower District, or anywhere in Fresno County, we ensure your electrical panel is safe, functional, and ready to protect your home."
        features={features}
        whatsIncluded={whatsIncluded}
        faqs={[
          {
            question: 'Why do I need breaker panel testing in Fresno?',
            answer:
              "Breaker panel testing identifies safety hazards before they cause fires or electrical failures. Circuit breakers can fail to trip properly, connections can loosen over time, panels can overheat, and some panel brands are known fire hazards. Regular testing ensures your Fresno home's electrical panel protects your family and property. Testing is especially important for homes over 25 years old or with frequent breaker trips.",
          },
          {
            question: 'How much does breaker panel testing cost in Fresno?',
            answer:
              'Breaker panel testing in Fresno typically costs $150–$300 depending on panel size and testing scope. Comprehensive inspections with thermal imaging and detailed testing may cost more. We provide upfront pricing and detailed reports showing panel condition, breaker performance, and any safety concerns for your Fresno home.',
          },
          {
            question: 'What does breaker panel testing in Fresno include?',
            answer:
              'Our Fresno panel testing includes circuit breaker performance testing, thermal imaging scans, connection tightness checks, voltage and amperage measurements, panel condition assessment, hazardous panel identification, code compliance review, and capacity evaluation. You receive a detailed report with photos, findings, and recommendations for repairs or upgrades.',
          },
          {
            question: 'How do I know if my electrical panel is dangerous in Fresno?',
            answer:
              "Warning signs include frequent breaker trips, breakers that won't reset, warm or hot panel, burning smell, buzzing sounds, rust or corrosion, and certain hazardous brands (Federal Pacific, Zinsco, Challenger). Our testing identifies these dangers. Many Fresno homes have outdated or hazardous panels that should be replaced for safety.",
          },
          {
            question: 'What are Federal Pacific and Zinsco panels and why are they dangerous?',
            answer:
              'Federal Pacific Electric (FPE) and Zinsco panels are known fire hazards. Their circuit breakers often fail to trip during overloads, allowing wires to overheat and cause fires. These panels were installed in many Fresno homes from the 1950s–1980s. If you have one, we strongly recommend replacement. Our testing identifies these hazardous panels and provides replacement estimates.',
          },
          {
            question: 'How long does breaker panel testing take in Fresno?',
            answer:
              'Most breaker panel testing takes 1–2 hours depending on panel size and testing scope. We test each breaker, scan the panel with thermal imaging, check all connections, and document findings. We schedule testing at your convenience and provide detailed reports within 24 hours for Fresno homeowners.',
          },
        ]}
      />
    </>
  );
}
